import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { settingsApi, GeneralSetting, BulkUpdateSettingsDto } from '@/lib/api/settings';
import { toast } from 'sonner';

export interface SettingsFormData {
  movidesk_api_url: string;
  movidesk_api_key: string;
  notification_email_host: string;
  notification_email_port: string;
  notification_email_user: string;
  notification_email_password: string;
  notification_email_to: string;
  enable_notifications: boolean;
}

export function useSettings() {
  const { t } = useTranslation();
  const [settings, setSettings] = useState<GeneralSetting[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);

  const fetchSettings = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await settingsApi.getAll();
      setSettings(data);
    } catch (err: any) {
      toast.error(err.response?.data?.message || t('settings.loadError'));
    } finally {
      setIsLoading(false);
    }
  }, [t]);

  const getSettingValue = useCallback(
    (name: string, defaultValue = '') => {
      const setting = settings.find((s) => s.name === name);
      return setting?.value || defaultValue;
    },
    [settings]
  );

  const getFormData = useCallback((): SettingsFormData => {
    const notificationEmails = getSettingValue('notification_email_to', '[]');
    let emailsArray: string[] = [];
    try {
      emailsArray = JSON.parse(notificationEmails);
    } catch {
      emailsArray = [];
    }

    const enableNotifications = getSettingValue('enable_notifications', 'false');
    const isEnabled = enableNotifications === 'true';

    return {
      movidesk_api_url: getSettingValue('movidesk_api_url'),
      movidesk_api_key: getSettingValue('movidesk_api_key'),
      notification_email_host: getSettingValue('notification_email_host'),
      notification_email_port: getSettingValue('notification_email_port', '587'),
      notification_email_user: getSettingValue('notification_email_user'),
      notification_email_password: getSettingValue('notification_email_password'),
      notification_email_to: emailsArray.join('\n'),
      enable_notifications: isEnabled,
    };
  }, [getSettingValue]);

  const saveSettings = useCallback(
    async (formData: SettingsFormData) => {
      try {
        setIsSaving(true);

        const emailsArray = formData.notification_email_to
          .split('\n')
          .map((email) => email.trim())
          .filter((email) => email.length > 0);

        const bulkUpdateData: BulkUpdateSettingsDto = {
          movidesk_api_url: formData.movidesk_api_url || undefined,
          movidesk_api_key: formData.movidesk_api_key || undefined,
          notification_email_host: formData.notification_email_host || undefined,
          notification_email_port: formData.notification_email_port
            ? parseInt(formData.notification_email_port)
            : undefined,
          notification_email_user: formData.notification_email_user || undefined,
          notification_email_password: formData.notification_email_password || undefined,
          notification_email_to: emailsArray.length > 0 ? emailsArray : undefined,
          enable_notifications: formData.enable_notifications ? 'true' : 'false',
        };

        const updatedSettings = await settingsApi.bulkUpdate(bulkUpdateData);
        setSettings(updatedSettings);
        toast.success(t('settings.saveSuccess'));
        return true;
      } catch (err: any) {
        toast.error(err.response?.data?.message || t('settings.saveError'));
        return false;
      } finally {
        setIsSaving(false);
      }
    },
    [t]
  );

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return {
    settings,
    isLoading,
    isSaving,
    getSettingValue,
    getFormData,
    saveSettings,
    refetch: fetchSettings,
  };
}
